import { useEffect, useRef, useState } from "react";
import {
  PUZZLE_BOARD_HEIGHT,
  PUZZLE_BOARD_WIDTH,
  PUZZLE_PLACED_Z_INDEX,
  PUZZLE_WORKSPACE_HEIGHT,
  clampPuzzlePosition,
  clearPuzzleProgress,
  createPuzzlePieces,
  isPuzzlePieceNearTarget,
  readPuzzleProgress,
  restorePuzzlePieces,
  savePuzzleProgress,
} from "../../domain/puzzle.js";

export function PuzzleBoard({
  imageUrl,
  rows = 3,
  columns = 3,
  onComplete,
  persistKey = null,
}) {
  const viewportRef = useRef(null);
  const [scale, setScale] = useState(1);
  const [pieces, setPieces] = useState([]);
  const [activePieceId, setActivePieceId] = useState(null);
  const draggedPieceId = useRef(null);
  const dragOffset = useRef({ x: 0, y: 0 });
  const currentPosition = useRef({ x: 0, y: 0 });
  const pieceElements = useRef({});
  const completionReported = useRef(false);
  const onCompleteRef = useRef(onComplete);

  const pieceWidth = PUZZLE_BOARD_WIDTH / columns;
  const pieceHeight = PUZZLE_BOARD_HEIGHT / rows;
  const paddingX = pieceWidth * 0.3;
  const paddingY = pieceHeight * 0.3;
  const xBounds = {
    minimum: -paddingX * 2,
    maximum: PUZZLE_BOARD_WIDTH - pieceWidth + paddingX * 2,
  };
  const yBounds = {
    minimum: -paddingY * 2,
    maximum: PUZZLE_WORKSPACE_HEIGHT - pieceHeight + paddingY * 2,
  };

  useEffect(() => {
    onCompleteRef.current = onComplete;
  }, [onComplete]);

  useEffect(() => {
    const resize = () => {
      const viewport = viewportRef.current;
      if (!viewport) return;
      const width = viewport.clientWidth;
      const height = viewport.clientHeight || window.innerHeight * 0.7;
      setScale(
        Math.min(width / PUZZLE_BOARD_WIDTH, height / PUZZLE_WORKSPACE_HEIGHT) *
          0.95,
      );
    };
    resize();
    const observer = new ResizeObserver(resize);
    if (viewportRef.current) observer.observe(viewportRef.current);
    window.addEventListener("resize", resize);
    return () => {
      observer.disconnect();
      window.removeEventListener("resize", resize);
    };
  }, []);

  useEffect(() => {
    completionReported.current = false;
    const generated = createPuzzlePieces({ rows, columns });
    const restored = restorePuzzlePieces(
      generated,
      readPuzzleProgress(persistKey),
    );
    setPieces([...restored].sort(() => Math.random() - 0.5));
    if (restored.length > 0 && restored.every((piece) => piece.isPlaced)) {
      completionReported.current = true;
      const timeout = window.setTimeout(() => onCompleteRef.current?.(), 300);
      return () => window.clearTimeout(timeout);
    }
    return undefined;
  }, [rows, columns, imageUrl, persistKey]);

  const startDrag = (event, piece) => {
    if (piece.isPlaced) return;
    event.preventDefault();
    event.currentTarget.setPointerCapture(event.pointerId);
    const highestZ = Math.max(...pieces.map((item) => item.zIndex), 0);
    setPieces((current) =>
      current.map((item) =>
        item.id === piece.id ? { ...item, zIndex: highestZ + 1 } : item,
      ),
    );
    setActivePieceId(piece.id);
    draggedPieceId.current = piece.id;
    dragOffset.current = {
      x: event.clientX / scale - piece.x,
      y: event.clientY / scale - piece.y,
    };
    currentPosition.current = { x: piece.x, y: piece.y };
  };

  const moveDrag = (event) => {
    const pieceId = draggedPieceId.current;
    if (!pieceId) return;
    const x = clampPuzzlePosition(
      event.clientX / scale - dragOffset.current.x,
      xBounds.minimum,
      xBounds.maximum,
    );
    const y = clampPuzzlePosition(
      event.clientY / scale - dragOffset.current.y,
      yBounds.minimum,
      yBounds.maximum,
    );
    currentPosition.current = { x, y };
    const element = pieceElements.current[pieceId];
    if (element) element.style.transform = `translate3d(${x}px, ${y}px, 0)`;
  };

  const finishDrag = (pieceId) => {
    if (!pieceId || draggedPieceId.current !== pieceId) return;
    draggedPieceId.current = null;
    setActivePieceId(null);
    const { x, y } = currentPosition.current;

    setPieces((current) => {
      const dragged = current.find((piece) => piece.id === pieceId);
      if (!dragged) return current;
      const next = current.map((piece) => {
        if (piece.id !== pieceId) return piece;
        if (!isPuzzlePieceNearTarget(piece, x, y)) return { ...piece, x, y };
        return {
          ...piece,
          x: piece.correctX,
          y: piece.correctY,
          isPlaced: true,
          zIndex: PUZZLE_PLACED_Z_INDEX,
        };
      });
      if (next.every((piece) => piece.isPlaced)) {
        clearPuzzleProgress(persistKey);
        if (!completionReported.current) {
          completionReported.current = true;
          window.setTimeout(() => onCompleteRef.current?.(), 300);
        }
      } else savePuzzleProgress(persistKey, next);
      return next;
    });
  };

  return (
    <div className="puzzle-board-viewport" ref={viewportRef}>
      <div
        className="puzzle-workspace"
        style={{ transform: `scale(${scale})` }}
        onPointerMove={moveDrag}
        onPointerUp={() => finishDrag(draggedPieceId.current)}
        onPointerCancel={() => finishDrag(draggedPieceId.current)}
      >
        <div className="puzzle-target" aria-hidden="true" />
        {pieces.map((piece) => (
          <div
            aria-label={`Peça ${piece.row + 1}, ${piece.column + 1}`}
            className={`puzzle-piece ${piece.isPlaced ? "placed" : ""}`}
            key={piece.id}
            onLostPointerCapture={() => finishDrag(piece.id)}
            onPointerDown={(event) => startDrag(event, piece)}
            ref={(element) => {
              if (element) pieceElements.current[piece.id] = element;
              else delete pieceElements.current[piece.id];
            }}
            role="img"
            style={{
              width: pieceWidth + paddingX * 2,
              height: pieceHeight + paddingY * 2,
              zIndex: piece.zIndex,
              cursor: piece.isPlaced
                ? "default"
                : activePieceId === piece.id
                  ? "grabbing"
                  : "grab",
              WebkitClipPath: `path('${piece.path}')`,
              clipPath: `path('${piece.path}')`,
              transform: `translate3d(${piece.x}px, ${piece.y}px, 0)`,
              willChange: activePieceId === piece.id ? "transform" : "auto",
              transition:
                activePieceId === piece.id
                  ? "none"
                  : "transform 0.15s ease-out",
              backgroundImage: `url("${imageUrl}")`,
              backgroundSize: `${PUZZLE_BOARD_WIDTH}px ${PUZZLE_BOARD_HEIGHT}px`,
              backgroundPosition: `${-piece.correctX}px ${-piece.correctY}px`,
            }}
          >
            <span aria-hidden="true" />
          </div>
        ))}
      </div>
    </div>
  );
}
